import { useEffect, useRef } from "react";

import { Loader } from "@/components/ui/loader";
import { getApiUrl } from "@/lib/api";
import { useRequisition } from "./RequisitionContext";

const getFileUrl = (
  accessionId: number,
  labId: number,
  options?: {
    selectedSamples?: number[];
    format?: "html" | "pdf";
  },
) => {
  const url = new URL(
    getApiUrl(`/api/requisition/${accessionId}`),
    window.location.origin,
  );

  url.searchParams.set("labId", labId.toString());
  url.searchParams.set("format", options?.format ?? "html");

  if (options?.selectedSamples) {
    url.searchParams.set(
      "selectedSamples",
      JSON.stringify(options.selectedSamples),
    );
  }

  url.hash = "#toolbar=1";
  return url.toString();
};

const useGetFileUrl = (
  accessionId: number,
  labId: number,
  options?: {
    selectedSamples?: number[];
    format?: "html" | "pdf";
  },
) => {
  return getFileUrl(accessionId, labId, options);
};

export function RequisitionPreview() {
  const { accession, labId, loading, setLoading, selectedSamples } =
    useRequisition();
  const ref = useRef<HTMLIFrameElement>(null);
  const urlRef = useRef<string>(undefined);

  const fileUrl = useGetFileUrl(accession.id, labId, {
    selectedSamples: selectedSamples.length > 0 ? selectedSamples : undefined,
    format: "pdf",
  });

  const onLoad = () => {
    setLoading(false);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: I want to re-run on selectedSamples change
  useEffect(() => {
    if (!loading && ref.current && urlRef.current !== fileUrl) {
      setLoading(true);
      urlRef.current = fileUrl;
      ref.current.contentWindow?.location.replace(fileUrl);
    }
  }, [fileUrl, loading, setLoading, selectedSamples]);

  return (
    <>
      {loading && (
        <div className="flex items-center justify-center h-full max-w-3xl w-full border bg-accent rounded-md">
          <Loader className="size-8" />
        </div>
      )}
      <iframe
        ref={ref}
        title={`Requisition - ${accession.id}`}
        style={{
          maxWidth: 800,
          display: loading ? "none" : "block",
          borderRadius: 6,
        }}
        className="border"
        height="100%"
        width="100%"
        onLoad={onLoad}
      />
    </>
  );
}
